import { useState } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import KeyboardArrowUpRounded from '@mui/icons-material/KeyboardArrowUpRounded';
import KeyboardArrowDownRounded from '@mui/icons-material/KeyboardArrowDownRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import DragIndicatorRounded from '@mui/icons-material/DragIndicatorRounded';
import MusicNoteRounded from '@mui/icons-material/MusicNoteRounded';
import { useJamRoom } from '../hooks/useJamRoom.jsx';
import { fmtDuration, fmtTime } from '../lib/format.js';
import TrackCover from './TrackCover.jsx';

/**
 * Cola colaborativa:
 *  - Pista en curso destacada arriba con ecualizador.
 *  - Drag & drop nativo + botones subir/bajar para reordenar (anfitrion o
 *    dueno, siempre detras de la pista sonando).
 *  - El estado canonico llega por `queue:state`: aqui solo se pide y pinta.
 */
export default function QueueList() {
  const jam = useJamRoom();
  const { queue, currentIndex, isHost, me } = jam;
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  const canTouch = (i) =>
    i > currentIndex && i >= 0 && i < queue.length && (isHost || queue[i]?.addedBy === me.id);

  async function requestReorder(from, to) {
    try {
      await jam.reorderQueue(from, to);
    } catch (err) {
      jam.pushToast(err.message || 'No se pudo reordenar', 'error');
      jam.syncRequest().catch(() => {});
    }
  }

  function onDragStart(e, i) {
    if (!canTouch(i)) {
      e.preventDefault();
      return;
    }
    setDragIndex(i);
    setOverIndex(i);
    e.dataTransfer.effectAllowed = 'move';
    try {
      e.dataTransfer.setData('text/plain', String(i));
    } catch {
      /* navegadores antiguos */
    }
  }

  function onDragOverRow(e, i) {
    if (dragIndex == null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (i !== overIndex) setOverIndex(i);
  }

  function onDropRow(e, i) {
    e.preventDefault();
    const from = dragIndex;
    const hovered = i;
    setDragIndex(null);
    setOverIndex(null);
    if (from == null || from === hovered || !canTouch(from)) return;
    const to = from < hovered ? hovered - 1 : hovered;
    if (to === from || to < 0) return;
    requestReorder(from, to);
  }

  function onDragEndClear() {
    setDragIndex(null);
    setOverIndex(null);
  }

  function moveNeighbor(i, dir) {
    const to = i + dir;
    if (!canTouch(i) || !canTouch(to)) return;
    requestReorder(i, to);
  }

  async function removeAt(i) {
    try {
      await jam.removeFromQueue(i);
    } catch (err) {
      jam.pushToast(err.message || 'No se pudo quitar la canción', 'error');
    }
  }

  const current = jam.currentTrack;
  const upcoming = queue.map((track, i) => ({ track, i })).filter(({ i }) => i !== currentIndex);
  const playing = jam.playback?.playing;

  return (
    <Paper
      elevation={0}
      component="section"
      aria-label="Cola de reproducción"
      onDragEnd={onDragEndClear}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        borderRadius: '24px',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'rgba(30,31,32,0.72)',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">Cola</Typography>
        <Chip size="small" label={queue.length} variant="outlined" />
      </Box>

      <Box sx={{ px: 1, pb: 1 }}>
        {current ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.75,
              p: 1.5,
              borderRadius: '20px',
              border: '1px solid',
              borderColor: 'rgba(168,199,250,0.20)',
              background:
                'linear-gradient(135deg, rgba(168,199,250,0.16) 0%, rgba(168,199,250,0.03) 60%)',
            }}
          >
            <TrackCover src={current.thumbnail} alt={current.title} size="lg" />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="overline" color="primary.main">
                Sonando ahora
              </Typography>
              <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600 }}>
                {current.title}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {current.artist}
                </Typography>
                <span className={`eq${playing ? '' : ' eq--paused'}`} aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </span>
                <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {fmtTime(jam.getPlaybackNow()?.positionSec ?? 0)}
                </Typography>
              </Stack>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: 1.5,
              borderRadius: '20px',
              border: '1px dashed',
              borderColor: 'divider',
              color: 'text.secondary',
            }}
          >
            <MusicNoteRounded />
            <Typography variant="body2">
              Nada suena todavía: busca una canción y añádela para empezar.
            </Typography>
          </Box>
        )}
      </Box>

      <Divider />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: { xs: 'visible', md: 'auto' }, px: 1, py: 1 }}>
        {upcoming.map(({ track, i }) => {
          const done = i < currentIndex;
          const touch = canTouch(i);
          return (
            <Box
              key={`${track.videoId}-${i}`}
              draggable={touch}
              onDragStart={(e) => onDragStart(e, i)}
              onDragOver={(e) => onDragOverRow(e, i)}
              onDrop={(e) => onDropRow(e, i)}
              title={touch ? 'Arrastra para reordenar' : undefined}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                px: 1,
                py: 0.75,
                borderRadius: '14px',
                cursor: touch ? 'grab' : 'default',
                opacity: done ? 0.45 : 1,
                transition: 'background-color .15s, opacity .15s',
                '&:hover': { bgcolor: 'action.hover', '& .row-actions': { opacity: 1 } },
                ...(dragIndex === i && { opacity: 0.4 }),
                ...(overIndex === i &&
                  dragIndex != null &&
                  dragIndex !== i && { boxShadow: 'inset 0 0 0 2px #a8c7fa' }),
              }}
            >
              {touch ? (
                <DragIndicatorRounded sx={{ fontSize: 16, color: 'text.disabled' }} />
              ) : (
                <Box sx={{ width: 16 }} />
              )}
              <Typography
                variant="caption"
                sx={{
                  width: 20,
                  textAlign: 'right',
                  color: 'text.disabled',
                  fontVariantNumeric: 'tabular-nums',
                  flex: '0 0 auto',
                }}
              >
                {i + 1}
              </Typography>
              <TrackCover src={track.thumbnail} alt={track.title} size="sm" />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                  {track.title}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap component="div">
                  {track.artist}
                  {track.addedByName ? ` · ${track.addedByName}` : ''}
                </Typography>
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontVariantNumeric: 'tabular-nums', flex: '0 0 auto' }}
              >
                {fmtDuration(track.durationSec)}
              </Typography>
              <Box
                className="row-actions"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.25,
                  opacity: { xs: 1, md: 0 },
                  transition: 'opacity .15s',
                  flex: '0 0 auto',
                }}
              >
                {touch && (
                  <Tooltip title="Subir">
                    <span>
                      <IconButton
                        size="small"
                        aria-label="Subir canción"
                        onClick={() => moveNeighbor(i, -1)}
                        disabled={i - 1 <= currentIndex}
                      >
                        <KeyboardArrowUpRounded fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                {touch && (
                  <Tooltip title="Bajar">
                    <span>
                      <IconButton
                        size="small"
                        aria-label="Bajar canción"
                        onClick={() => moveNeighbor(i, 1)}
                        disabled={i + 1 >= queue.length}
                      >
                        <KeyboardArrowDownRounded fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                {touch && (
                  <Tooltip title="Quitar de la cola">
                    <IconButton
                      size="small"
                      color="error"
                      aria-label="Quitar de la cola"
                      onClick={() => removeAt(i)}
                    >
                      <DeleteOutlineRounded fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          );
        })}

        {upcoming.length === 0 && current && (
          <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 2.5, textAlign: 'center' }}>
            Fin de la fila: añade más música para continuar.
          </Typography>
        )}
      </Box>
    </Paper>
  );
}
